/**************************************************************************************************/
/** @import Node Packages                                                                        **/
/**************************************************************************************************/
import { defineComponent } from 'vue'

/**************************************************************************************************/
/** @import .                                                                                    **/
/**************************************************************************************************/
import dataFormElements from './dataform.elements'
import * as Parsers from './response.parser'

/**************************************************************************************************/
/** @import Components/Global                                                                    **/
/**************************************************************************************************/
import * as BasicHeader from '@/Components/Global/BasicHeader/template.vue'
import * as DataForm from '@/Components/Global/DataForm/template.vue'
import * as DataTable from '@/Components/Global/DataTable/template.vue'
import * as PopupUserForm from '@/Components/Global/PopupUserForm/template.vue'
import * as SideMenu from '@/Components/Global/SideMenu/template.vue'
import * as StatusBar from '@/Components/Global/StatusBar/template.vue'

/**************************************************************************************************/
/** @import Components/Modules                                                                   **/
/**************************************************************************************************/
import * as AdminNavigation from '@/Components/Modules/Administration/AdminNavigation/template.vue'
import * as StorageFormQR from '@/Components/Modules/Administration/StorageFormQR/template.vue'

/**************************************************************************************************/
/** @import Constants                                                                            **/
/**************************************************************************************************/
import { AppModules } from '@/Constants/AppModules'
import { AppValues } from '@/Constants/AppValues'
import { Breakpoints } from '@/Constants/Breakpoints'
import { Component } from '@/Constants/Component'
import { Documents } from '@/Constants/Documents'
import { Server } from '@/Constants/Server'
import { VueRouter } from '@/Constants/VueRouter'
import { Vuex } from '@/Constants/Vuex'

/**************************************************************************************************/
/** @import Helpers/HTTP                                                                         **/
/**************************************************************************************************/
import { AxiosHelper } from '@/Helpers/HTTP/AxiosHelper'

/**************************************************************************************************/
/** @import Helpers/Utils                                                                        **/
/**************************************************************************************************/
import { DataParsers } from '@/Helpers/Utils/DataParsers'
import { JoiHelper } from '@/Helpers/Utils/JoiHelper'
import { QRHelper } from '@/Helpers/Utils/QRHelper'
import { StoreHelper } from '@/Helpers/Utils/StoreHelper'
import { TypesHelper } from '@/Helpers/Utils/TypesHelper'

/**************************************************************************************************/
/** @import Mixins                                                                               **/
/**************************************************************************************************/
import MixinBase from '@/Mixins/MixinBase'
import MixinComponent from '@/Mixins/MixinComponent'
import MixinFetch from '@/Mixins/MixinFetch'
import MixinResponsive from '@/Mixins/MixinResponsive'

/**************************************************************************************************/
/** @import Store/Global                                                                         **/
/**************************************************************************************************/
import Store from '@/Store/Global/Default'

/**************************************************************************************************/
/** @internal Const @name AdministrationStoragesView                                             **/
/**************************************************************************************************/
const AdministrationStoragesView = defineComponent(
{
	/*************************************************/
	/** @Properties                                 **/
	/*************************************************/
	name: VueRouter.Views.Modules.Administration.StoragesView.NAME, mixins: [MixinBase, MixinComponent, MixinFetch, MixinResponsive],

	components:
	{
		AdminNavigation: AdminNavigation.default,
		BasicHeader: BasicHeader.default,
		DataTable: DataTable.default,
		PopupUserForm: PopupUserForm.default,
		SideMenu: SideMenu.default,
		StatusBar: StatusBar.default,
		StorageFormQR: StorageFormQR.default
	},

	/*************************************************/
	/** @Data                                       **/
	/*************************************************/
	data()
	{
		return {
			dataForms:
			{
				['Companies']: dataFormElements.Companies,
				['Storages']: dataFormElements.Storages
			},
			sideMenu:
			{
				items:
				[
					{ type: 'button', label: 'Plantas', componentType: 'PopupUserForm', componentName: 'Storages', icon: 'warehouse', isActive: true },
					{ type: 'button', label: 'Clientes', componentType: 'PopupUserForm', componentName: 'Companies', icon: 'building', isActive: false }
				]
			},
			states:
			{
				componentType: 'PopupUserForm',
				componentName: 'Storages',
				dataFormAction: Component.Actions.INSERT,
				dataTableParser: Parsers.parseStoragesResponse,
				documentToUpdate: undefined as any,
				inputButtonKey: undefined as string | undefined,
				isMobile: false,
				showDataTable: true,
				showPopupUserForm: false,
				showStorageFormQR: false
			}
		}
	},

	/*************************************************/
	/** @Hooks                                      **/
	/*************************************************/
	beforeDestroy() : void
	{
		(Store.getters.getStoredAHFetchCompaniesForPage as AxiosHelper)?.abortRequest();
		(Store.getters.getStoredAHFetchStoragesForPage as AxiosHelper)?.abortRequest()
	},

	mounted() : void
	{
		this.InitDataTable()
		this.InitDataForm()
		this.InitPermissions()

		// Deshabilitar comportamiento de Busqueda Local y Tipo de Descarga para el componente 'DataTable'.
		this.DataTable.Set<DataTable.DataTableRef['states']>({ exportDataAsAsync: true, exportDataFilterType: AppValues.DataTableExportDataFilterType.QUANTITY, isLocalSearch: false })

		// Establecer las propiedades para el componente <SideMenu>.
		this.SideMenu.Initialize(this.sideMenu)
		this.SideMenuMobile.Initialize(this.sideMenu)
		this.SideMenuMobile.Set<SideMenu.SideMenuRef['states']>({ isMobile: true })

		// Componente PopupTable/DataTable
		this.NestedDataTable.Set<DataTable.DataTableRef['states']>({ preventDefaultStacked: true })
		this.NestedDataTable.SetSelectable(true)

		// Component AdministrationStoragesView/DataTable
		this.DataTable.Set<DataTable.DataTableRef['states']>({ preventDefaultStacked: true })
	},

	/*************************************************/
	/** @Computed                                   **/
	/*************************************************/
	computed:
	{
		BasicHeader() : BasicHeader.BasicHeaderRef
		{
			return this.$refs.basicHeader as BasicHeader.BasicHeaderRef
		},

		DataForm() : DataForm.DataFormRef
		{
			return this.PopupUserForm.$refs.dataForm as DataForm.DataFormRef
		},

		DataTable() : DataTable.DataTableRef
		{
			return this.$refs.dataTable as DataTable.DataTableRef
		},

		NestedDataTable() : DataTable.DataTableRef
		{
			return this.PopupUserForm.DataForm.PopupTable.DataTable as DataTable.DataTableRef
		},

		PopupUserForm() : PopupUserForm.PopupUserFormRef
		{
			return this.$refs.popupUserForm as PopupUserForm.PopupUserFormRef
		},

		SideMenu() : SideMenu.SideMenuRef
		{
			return this.$refs.sideMenu as SideMenu.SideMenuRef
		},

		SideMenuMobile() : SideMenu.SideMenuRef
		{
			return this.$refs.sideMenuMobile as SideMenu.SideMenuRef
		},

		StatusBar() : StatusBar.StatusBarRef
		{
			return this.$refs.statusBar as StatusBar.StatusBarRef
		},

		StorageFormQR() : StorageFormQR.StorageFormQRRef
		{
			return this.$refs.storageFormQR as StorageFormQR.StorageFormQRRef
		}
	},

	/*************************************************/
	/** @Methods                                    **/
	/*************************************************/
	methods:
	{
		HandleCancelRequests(componentName: string) : void
		{
			switch (componentName)
			{
				case 'Companies':
				{
					(Store.getters.getStoredAHFetchCompaniesForPage as AxiosHelper)?.abortRequest()
					break
				}
				case 'Storages':
				{
					(Store.getters.getStoredAHFetchStoragesForPage as AxiosHelper)?.abortRequest()
					break
				}
			}
		},

		HasStorageAccessTotem() : void
		{
			// Comprobación de propiedad 'AccessTotem'.
			const { documentToUpdate } = this.states
			const hasAccess = documentToUpdate?.accessTotem === 'Habilitado'

			// Si se tiene acceso, autogenerar el 'Código QR'.
			if (hasAccess)
			{
				const stringToEncode = QRHelper.encodeStorageAccessTotem(documentToUpdate?.code)
				const stringLabel    = `QR_LOGIN_${ documentToUpdate?.code }`
				this.StorageFormQR.Set<StorageFormQR.StorageFormQRRef['states']>({ hasAccess, dataQR: stringToEncode, downloadName: stringLabel, labelQR: stringLabel })
				this.StorageFormQR.GenerateQR()
			}
			else
			{
				this.StorageFormQR.ResetQR()
				this.StorageFormQR.GenerateQR()
			}
		},

		InitDataForm() : void
		{
			this.StorageFormQR.DataForm.Initialize(dataFormElements[this.states.componentName])
		},

		async InitDataTable(page = 1, forceRefresh = false) : Promise<void>
		{
			if (forceRefresh) this.DataTable.ClearAll()
			const { componentName } = this.states
			const responseType = componentName === 'Companies' ? 'empresas cliente' : 'plantas'

			this.DataTable.SetFetchingState(true)
			this.StatusBar.SetStatus(true, `Obteniendo listado de ${ responseType }...`)
			this.StatusBar.SetElapsedTime(false)

			// Respuesta de la Acción donde se obtienen los Registros.
			const sPerformance = performance.now()
			const response     = await Store.dispatch(`fetch${ componentName }ForPage`, { forceRefresh, page })
			const ePerformance = performance.now()

			// Si se obtiene una respuesta válida, continuar con el proceso.
			if (response)
			{
				// Procesar la información y actualizar el Componente.
				const { fields, items, actions } = this.states.dataTableParser(response.data)
				this.DataTable.UpdateElementsAndPagination(response.totalPages, fields, items, actions)

				this.DataTable.SetFetchingState(false)
				this.StatusBar.SetStatus(false, `Resultado: Obtenido correctamente un total de ${ response.data.length } ${ responseType }`, 'success')
				this.StatusBar.SetElapsedTime(true, sPerformance, ePerformance)
			}
		},

		InitPermissions() : void
		{
			const { componentName } = this.states
			const userPermissions = Store.getters.getStoredUserPermissionsAsObject

			switch (componentName)
			{
				case 'Companies':
					this.DataTable.SetPermission('ACTION_EDIT', userPermissions.MODULE_20_MANAGE_COMPANIES?.privileges.write)
					this.DataTable.SetPermission('NEW_BUTTON', userPermissions.MODULE_20_MANAGE_COMPANIES?.privileges.write)
					break
				case 'Storages':
					this.DataTable.SetPermission('ACTION_EDIT', userPermissions.MODULE_20_MANAGE_STORAGES?.privileges.write)
					this.DataTable.SetPermission('NEW_BUTTON', userPermissions.MODULE_20_MANAGE_STORAGES?.privileges.write)
					break
			}
		},

		InitRegions() : void
		{
			// Generar las opciones de los <Select> si existen regiones en la Store.
			const { DataForm } = this.StorageFormQR
			const regions = Store.getters.getStoredRegions
			DataForm.ClearOptions('commune')

			if (regions !== undefined)
			{
				// Limpiar los arrays para evitar duplicidad de opciones.
				DataForm.ClearOptions('region')
				regions.forEach((x: any) => DataForm.AddOption('region', { value: x.regiones, text: x.regiones }))
			}
		},

		OnButtonClick(componentType: string, componentName: string) : void
		{
			this.Set<AdministrationStoragesViewRef['states']>({ componentName, componentType })
			this.InitDataForm()

			this.DataTable.ClearSortBy()
			this.Set<AdministrationStoragesViewRef['states']>({ dataTableParser: this.ResolveDataTableParser(this.DataTable.states.stacked) })
			this.DataTable.SetPaginationPage(1)
		},

		OnClose() : void
		{
			this.PopupUserForm.Set<PopupUserForm.PopupUserFormRef['states']>({ isFetching: false })
			this.Set<AdministrationStoragesViewRef['states']>({ showPopupUserForm: false })
			this.PopupUserForm.DataForm.ClearInputs()
		},

		OnDataFormSubmit() : Promise<void> | undefined
		{
			const { componentName } = this.states
			switch (componentName)
			{
				case 'Companies': return this.UpsertCompany()
				case 'Storages': return this.UpsertStorage()
			}
		},

		async OnDTButtonClick(key: string, { item }: any) : Promise<void>
		{
			if (key === 'edit')
			{
				// Almacenar en la propiedad 'documentToUpdate' la fila a actualizar.
				this.Set<AdministrationStoragesViewRef['states']>({ dataFormAction: Component.Actions.UPDATE, documentToUpdate: item, showStorageFormQR: true })
				const { componentName, documentToUpdate } = this.states

				// Cambiar los estados a 'update' y la visibilidad en 'true'
				const { DataForm } = this.StorageFormQR
				DataForm.Set<DataForm.DataFormRef['states']>({ action: Component.Actions.UPDATE })

				// Array con valores no validos para comparar el contenido del campo correspondiente,
				// y evitar establecer valores no deseados en el componente 'DataForm'.
				const invalidValues = [undefined, null, '', AppValues.Strings.DEFAULT_EMPTY_STRING]

				if (componentName === 'Storages')
				{
					// Comprobación de propiedad 'AccessTotem'
					this.HasStorageAccessTotem()

					// Autocompletar los Inputs al momento de editar.
					for (let key of ['code', 'name', 'companyName', 'accessTotem'])
					{
						const value = documentToUpdate[key]
						if (!TypesHelper.isValueInArray(value, invalidValues)) DataForm.SetValue(key, value)
					}
				}
			}
		},

		OnDTCurrentSearchPaginationChanged(page: number, searchParams: any) : void
		{
			this.OnDTSearchButtonClicked(searchParams, page)
		},

		async OnDTExportDataClick(format: string, filterType: AppValues.DataTableExportDataFilterType, filterValue: AppValues.QuantityChoiceList) : Promise<void>
		{
			// Datos requeridos para obtener los Registros a Descargar.
			const { _idAdminCompany } = Store.getters.getStoredUser
			const { componentName } = this.states

			// Objeto con las Propiedades requeridas por la Petición.
			const params =
			{
				_idAdminCompany,
				itemsPerPage: 1,
				page: 1,
				quantity: filterValue
			}

			// 'Parser' utilizado para procesar la Respuesta de los Datos.
			const { dataTableParser } = this.states
			let response: any

			// Realizar la Petición al servidor.
			if (componentName === 'Companies')
			{
				response = await this.Fetch(Server.Fetching.Method.GET, Server.Routes.Companies.GetCompaniesByPage, params)
			}
			if (componentName === 'Storages')
			{
				response = await this.Fetch(Server.Fetching.Method.GET, Server.Routes.Storages.GetStoragesByPage, params)
			}

			// Si se obtiene una respuesta satisfactoria, continuar con el proceso.
			if (response.status === Server.Response.StatusCodes.SUCCESS)
			{
				// Respuesta de la Petición.
				const _response = response.data.body.data
				const { items } = dataTableParser(_response)
				this.DataTable.HandleExportDataType(format, items)
			}
		},

		async OnDTPaginationChanged(page: number) : Promise<void>
		{
			this.InitDataTable(page)
		},

		async OnDTRefreshButtonClick(currentPage: number) : Promise<void>
		{
			Store.commit(`destroy${ this.states.componentName }`)
			this.InitDataTable(currentPage, true)
		},

		OnDTSearchButtonClicked(searchParams: any, page = 1) : void
		{
			const { searchKey, searchValue } = searchParams
			const { componentName } = this.states
			const { _idAdminCompany } = Store.getters.getStoredUser

			// Asignar los parametros por pestaña.
			switch (componentName)
			{
				case 'Companies':
				{
					this.DataTable.DoInputSearch(Server.Routes.Companies.GetCompaniesBySearchFilter,
						this.states.dataTableParser,
						{
							_idAdminCompany, isClient: true,
							itemsPerPage: this.DataTable.itemsPerPage, page,
							searchKey, searchValue
						},
						(response) => response.data.body[0]
					)
					break
				}
				case 'Storages':
				{
					this.DataTable.DoInputSearch(Server.Routes.Storages.GetStoragesBySearchFilter,
						this.states.dataTableParser,
						{
							_idAdminCompany,
							itemsPerPage: this.DataTable.itemsPerPage, page,
							searchKey, searchValue
						},
						(response) => response.data.body[0]
					)
					break
				}
			}
		},

		OnInputButtonClick(key: string) : void
		{
			this.Set<AdministrationStoragesViewRef['states']>({ inputButtonKey: key })
			this.UpdatePopupComponents(false)
		},

		OnPTSelect(key: string, currentRow: any) : void
		{
			const { DataForm } = this.PopupUserForm
			const keyId = key === 'company' ? '_idCompany' : '_idUser'
			DataForm.SetValue(key, currentRow.item[keyId], '_id')
			DataForm.SetValue(key, currentRow.item.name, 'value')
		},

		OnPUFPaginationChanged(page: number) : void
		{
			this.UpdatePopupComponents(false, page)
		},

		OnPUFRefreshButtonClick(key: string) : void
		{
			this.UpdatePopupComponents(false, 1, true)
		},

		OnResponsiveBreakpoint(breakpoint: number) : void
		{
			this.BasicHeader.Set<BasicHeader.BasicHeaderRef['states']>({ isMobile: breakpoint <= Breakpoints.Medium })
			this.DataTable.Set<DataTable.DataTableRef['states']>({ stacked: breakpoint <= Breakpoints.XLarge })
			this.NestedDataTable.Set<DataTable.DataTableRef['states']>({ stacked: breakpoint <= Breakpoints.Large })

			this.Set<AdministrationStoragesViewRef['states']>({ isMobile: breakpoint <= Breakpoints.Medium, dataTableParser: this.ResolveDataTableParser(breakpoint <= Breakpoints.XLarge) })
			this.InitDataTable()
			this.UpdatePopupComponents(true)
		},

		OnServerCaughtFetchException(path: string, resolution: Server.Fetching.Resolutions, status: number, extras?: any) : void
		{
			// Controlar las excepciones de las diferentes peticiones realizadas en la vista.
			if (path === Server.Routes.Companies.GetCompanies)
			{
				this.ShowToast('Sin Registros', 'No existen Empresas para mostrar!', 'danger')
			}
			else if (path === Server.Routes.Storages.GetStorages)
			{
				this.ShowToast('Sin Registros', 'No existen Ubicaciones para mostrar!', 'danger')
			}
			else if (path === Server.Routes.Users.GetUsers)
			{
				this.ShowToast('Sin Registros', 'No existen Usuarios para mostrar!', 'danger')
			}
			this.PopupUserForm.Set<PopupUserForm.PopupUserFormRef['states']>({ isFetching: false })
		},

		OnServerFailedResponse(path: string) : void
		{
			this.PopupUserForm.Set<PopupUserForm.PopupUserFormRef['states']>({ isFetching: false })
		},

		OnSFQRClick(key: string) : void
		{
			switch (key)
			{
				case 'CLOSE':
				{
					this.Set<AdministrationStoragesViewRef['states']>({ showStorageFormQR: false })
					this.StorageFormQR.DataForm.ClearInputs()
					break
				}
				case 'GENERATE_QR':
				{
					this.StorageFormQR.GenerateQR()
					break
				}
				case 'DOWNLOAD_PNG':
				{
					this.StorageFormQR.DownloadQR()
					break
				}
			}
		},

		async OnSFQRSubmitClick(hasAccess: boolean) : Promise<void>
		{
			const { documentToUpdate } = this.states
			const accessTotem          = !hasAccess
			const accessTotemString    = accessTotem ? 'Habilitando' : 'Deshabilitando'

			// Objeto con las Propiedades requeridas por la Petición.
			const body =
			{
				_idStorage: documentToUpdate?._idStorage,
				accessTotem
			}

			// Realizar la Petición al servidor.
			this.StorageFormQR.Set<StorageFormQR.StorageFormQRRef['states']>({ isFetching: true })
			this.ShowToast('Actualizando Planta', `${ accessTotemString } acceso Totem para Planta ${ documentToUpdate?.code }`, 'warning')
			const response = await this.Fetch(Server.Fetching.Method.PATCH, Server.Routes.Storages.UpdateStorageAccessTotem, body)

			// Si se obtiene una respuesta válida, continuar con el proceso.
			if (response?.status === Server.Response.StatusCodes.SUCCESS)
			{
				// Refrescar los datos del componente 'DataTable'.
				await this.InitDataTable(this.DataTable.currentPage, true)

				// Actualizar la referencia de la 'Planta' dentro del formulario.
				const { items } = this.states.dataTableParser(response.data.body)
				this.OnDTButtonClick('edit', { item: items[0] })
				this.StorageFormQR.Set<StorageFormQR.StorageFormQRRef['states']>({ isFetching: false })
				this.ShowToast(`Acceso ${ accessTotemString }`, `Acceso Totem para Planta ${ documentToUpdate?.code } actualizado correctamente`, 'success')
			}
		},

		OnShowPopupUserForm() : void
		{
			this.Set<AdministrationStoragesViewRef['states']>({ dataFormAction: Component.Actions.INSERT, showPopupUserForm: true })
			this.PopupUserForm.DataForm.Set<DataForm.DataFormRef['states']>({ action: this.states.dataFormAction })
			this.InitRegions()
		},

		ResolveCompaniesPath(action: number) : string
		{
			return action === Component.Actions.INSERT
				? Server.Routes.Companies.AddCompany
				: Server.Routes.Companies.UpdateCompany
		},

		ResolveDataTableParser(stackedOrDefault: boolean) : any
		{
			const { componentName } = this.states
			switch (componentName)
			{
				case 'Companies':
					return stackedOrDefault ? Parsers.parseStackedCompaniesResponse : Parsers.parseCompaniesResponse
				case 'Storages':
					return stackedOrDefault ? Parsers.parseStackedStoragesResponse : Parsers.parseStoragesResponse
			}
		},

		ResolveStoragesPath(action: number) : string
		{
			return action === Component.Actions.INSERT
				? Server.Routes.Storages.AddStorage
				: Server.Routes.Storages.UpdateStorage
		},

		async UpdatePopupComponents(preventShowPopupTable: boolean, page = 1, forceRefresh = false) : Promise<void>
		{
			// Propiedades.
			const { DataForm } = this.PopupUserForm
			const key = this.states.inputButtonKey

			// Validaciones Opcionales según Casos.
			if (!preventShowPopupTable) DataForm.Set<DataForm.DataFormRef['states']>({ showPopupTable: true })
			if (forceRefresh) this.NestedDataTable.ClearAll()
			this.NestedDataTable.SetFetchingState(true)

			if (key === 'company')
			{
				// Actualizar el titulo al componente 'PopupTable'.
				DataForm.PopupTable.SetTitle('Selección Empresa')

				// Eliminar los Registros si se fuerza una Actualización.
				if (forceRefresh) Store.commit('destroyCompanies')

				// Aplicar Registros con Paginación.
				const response = await Store.dispatch('fetchCompaniesForPage', { forceRefresh, page })
				const SummaryParser = DataParsers.Companies.GetSummaryParser(this.NestedDataTable.states.stacked)
				const { fields, items } = SummaryParser(response.data.filter((x: any) => x.type))
				this.NestedDataTable.UpdateElementsAndPagination(response.totalPages, fields, items)

				// Aplicar Orden para Columna especifica.
				this.NestedDataTable.ResetEmptyText()
				this.NestedDataTable.SortOrder('name', 'asc')
			}
			else if (key === 'technical' || key === 'userAdmin')
			{
				// Actualizar el titulo al componente 'PopupTable'.
				DataForm.PopupTable.SetTitle(`Selección de ${ key === 'technical' ? 'Técnico' : 'Administrador de Contrato' }`)

				// Eliminar los Registros si se fuerza una Actualización.
				if (forceRefresh) Store.commit('destroyUsers')

				// Aplicar Registros con Paginación.
				const response = await Store.dispatch('fetchUsersForPage', { forceRefresh, isClient: false, page, type: 'Internal' })
				const SummaryParser = DataParsers.Users.GetSummaryInternalsParser(this.NestedDataTable.states.stacked)
				const { fields, items } = SummaryParser(response.data.filter((x: any) => x._idRole !== Documents.Roles.Kreis.CLIENT && x.isValid !== false))
				this.NestedDataTable.UpdateElementsAndPagination(response.totalPages, fields, items)

				// Aplicar Orden para Columna especifica.
				this.NestedDataTable.ResetEmptyText()
				this.NestedDataTable.SortOrder('rut', 'asc')
			}
		},

		async UpsertCompany() : Promise<void>
		{
			// Referencias a Componentes y Datos.
			const { documentToUpdate } = this.states
			const { DataForm }         = this.PopupUserForm
			const { action }           = DataForm.states
			const isInserting          = action === Component.Actions.INSERT

			// Realizar validación de los Campos.
			if (!DataForm.ValidateStates())
			{
				this.ShowToast('Error de Validación', 'Verifica que ningun campo se encuentre marcado de color rojo.', 'danger')
				return
			}

			// Inputs del DataForm.
			const _idAdminCompany = Store.getters.getStoredUser._idAdminCompany
			const _idCompany      = documentToUpdate?._idCompany
			const code            = DataForm.GetValue('code')
			const rut             = DataForm.GetValue('rut')
			const name            = DataForm.GetValue('name')
			const address         = DataForm.GetValue('address')
			const commune         = DataForm.GetValue('commune')
			const region          = DataForm.GetValue('region')
			const type            = DataForm.GetValue<any>('type')[0]
			const isClient        = DataForm.GetValue('isClient') === 'Sí'
			const isValid         = DataForm.GetValue('isValid') === 'Sí'

			// Objeto con las Propiedades requeridas por la Petición.
			const body = action === Component.Actions.INSERT ?
			{
				_idAdminCompany, code, rut, name, address, region, commune, type, isClient
			} :
			{
				_idCompany, name, address, commune, region, isValid
			}

			// Validación de los campos de la petición.
			const joiSchema = isInserting ? AppModules.Collections.JoiSchemas.Companies.AddCompany : AppModules.Collections.JoiSchemas.Companies.UpdateCompany
			const result = joiSchema.validate(body)
			if (result.error) return JoiHelper.showToastOnError(this.ShowToast, `Error al ${ isInserting ? 'Crear una' : 'Actualizar la' } Empresa`, result.error)

			// Bloquear el bóton submit hasta obtener una respuesta.
			this.PopupUserForm.Set<PopupUserForm.PopupUserFormRef['states']>({ isFetching: true })

			// Realizar la Petición al servidor.
			const fetchAction = action === Component.Actions.INSERT ? Server.Fetching.Method.POST : Server.Fetching.Method.PATCH
			const response = await this.Fetch(fetchAction, this.ResolveCompaniesPath(action), body)

			// Si se obtiene una respuesta satisfactoria, continuar con el proceso.
			if (response?.status === Server.Response.StatusCodes.SUCCESS)
			{
				const { fields, items, actions } = this.states.dataTableParser(response.data.body)
				this.DataTable.SetFields(fields)
				this.DataTable.SetActions(actions)

				if (action === Component.Actions.INSERT)
				{
					Store.commit('addCompany', response.data.body[0])
					this.DataTable.AddRow(items[0])
					this.ShowToast('Creación de Registro', 'El registro a sido creado correctamente!', 'success')
				}
				else if (action === Component.Actions.UPDATE)
				{
					Store.commit('updateCompany', response.data.body[0])
					this.DataTable.UpdateRow(items[0], '_idCompany')
					this.ShowToast('Actualización de Registro', 'El registro a sido actualizado correctamente!', 'success')
				}

				this.OnClose()
				this.PopupUserForm.Set<PopupUserForm.PopupUserFormRef['states']>({ isFetching: false })
			}
		},

		async UpsertStorage() : Promise<void>
		{
			// Referencias a Componentes y Datos.
			const { documentToUpdate } = this.states
			const { DataForm }         = this.PopupUserForm
			const { action }           = DataForm.states
			const isInserting          = action === Component.Actions.INSERT

			// Inputs del DataForm.
			const _idStorage   = documentToUpdate?._idStorage
			const _idCompany   = DataForm.GetValue('company', '_id')
			const _idTechnical = DataForm.GetValue('technical', '_id') || null
			const _idUserAdmin = DataForm.GetValue('userAdmin', '_id') || null
			const codeLocation = DataForm.GetValue('codeLocation')
			const code         = DataForm.GetValue('code')
			const name         = DataForm.GetValue('name')
			const address      = DataForm.GetValue('address')
			const region       = DataForm.GetValue('region')  || null
			const commune      = DataForm.GetValue('commune') || null
			const isValid      = DataForm.GetValue('isValid') === 'Sí'

			// Objeto con las Propiedades requeridas por la Petición.
			const body = action === Component.Actions.INSERT ?
			{
				_idCompany, _idTechnical, _idUserAdmin, codeLocation, code, name, address, commune, region
			} :
			{
				_idStorage, _idCompany, _idTechnical, _idUserAdmin, code, name, address, region, commune, isValid
			}

			// Validación de los campos de la petición.
			const joiSchema = isInserting ? AppModules.Collections.JoiSchemas.Storages.AddStorage : AppModules.Collections.JoiSchemas.Storages.UpdateStorage
			const result = joiSchema.validate(body)
			if (result.error) return JoiHelper.showToastOnError(this.ShowToast, `Error al ${ isInserting ? 'Crear una' : 'Actualizar la' } Ubicación`, result.error)

			// Bloquear el bóton submit hasta obtener una respuesta.
			this.PopupUserForm.Set<PopupUserForm.PopupUserFormRef['states']>({ isFetching: true })

			// Realizar la Petición al servidor.
			const fetchAction = action === Component.Actions.INSERT ? Server.Fetching.Method.POST : Server.Fetching.Method.PATCH
			const response = await this.Fetch(fetchAction, this.ResolveStoragesPath(action), body)

			// Si se obtiene una respuesta satisfactoria, continuar con el proceso.
			if (response?.status === Server.Response.StatusCodes.SUCCESS)
			{
				const { fields, items, actions } = this.states.dataTableParser(response.data.body)
				this.DataTable.SetFields(fields)
				this.DataTable.SetActions(actions)

				if (action === Component.Actions.INSERT)
				{
					Store.commit('addStorage', response.data.body[0])
					this.DataTable.AddRow(items[0])
					this.ShowToast('Creación de Registro', 'El registro a sido creado correctamente!', 'success')
				}
				else if (action === Component.Actions.UPDATE)
				{
					Store.commit('updateStorage', response.data.body[0])
					this.DataTable.UpdateRow(items[0], '_idStorage')
					this.ShowToast('Actualización de Registro', 'El registro a sido actualizado correctamente!', 'success')
				}

				this.OnClose()
				this.PopupUserForm.Set<PopupUserForm.PopupUserFormRef['states']>({ isFetching: false })
			}
		}
	},

	/*************************************************/
	/** @Watch                                      **/
	/*************************************************/
	watch:
	{
		...StoreHelper.watchStoreProperty(Vuex.Modules.Global.Names.Permissions, 'InitPermissions')
	}
})

/**************************************************************************************************/
/** @export Component @name AdministrationStoragesView                                           **/
/**************************************************************************************************/
export default AdministrationStoragesView

/**************************************************************************************************/
/** @export Type @name AdministrationStoragesViewRef                                             **/
/**************************************************************************************************/
export type AdministrationStoragesViewRef = InstanceType<typeof AdministrationStoragesView>